import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, Heart } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";

const IDEAS = ["Thinking of you 💜", "Proud of you for showing up this week", "Here if you want to talk"];

export const SupporterNoteForm = ({ token, name }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [tough, setTough] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get(`/supporters/view/${token}/tough-day`).then(({ data }) => {
      const r = params.get("reply");
      const pick = r !== null ? data.replies[Number(r)] : undefined;
      setTough(data.today || pick ? data.replies : null);
      if (pick) {
        setMsg(pick);
        setTimeout(() => document.getElementById("note")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      }
    }).catch((e) => logError("Tough-day status failed:", e));
  }, [token, params]);

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/supporters/view/${token}/note`, { message: msg.trim() });
      setSent(true);
      setMsg("");
    } catch (err) {
      toast.error(errMsg(err, "Could not send note"));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div id="note" className="mm-card p-6 text-center" data-testid="supporter-note-sent">
        <Heart className="mx-auto text-mm-error fill-current" />
        <p className="font-semibold mt-2">Your note is on its way to {name}'s home screen.</p>
        <button className="text-sm text-mm-primary font-semibold mt-3" onClick={() => setSent(false)} data-testid="supporter-note-another">Send another</button>
      </div>
    );
  }
  return (
    <form id="note" onSubmit={send} className="mm-card p-6" data-testid="supporter-note-form">
      <h2 className="font-semibold">Send {name} an encouraging note</h2>
      <p className="text-sm text-mm-muted mt-1">It shows up on their home screen. Keep it short and kind.</p>
      {tough && (
        <div className="mm-soft p-3 mt-3" data-testid="supporter-tough-day">
          <div className="text-sm font-semibold">🌤️ {name} is having a tough day — a kind note could help</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {tough.map((t, i) => <button type="button" key={t} className={`mm-chip !text-xs ${msg === t ? "mm-chip-on" : ""}`} onClick={() => setMsg(t)} data-testid={`supporter-tough-reply-${i}`}>{t}</button>)}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        {IDEAS.map((t, i) => <button type="button" key={t} className="mm-chip !text-xs" onClick={() => setMsg(t)} data-testid={`supporter-note-idea-${i}`}>{t}</button>)}
      </div>
      <textarea className="mm-input min-h-[100px] mt-3" maxLength={500} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a few words…" data-testid="supporter-note-input" />
      <div className="flex justify-between items-center mt-3">
        <span className="text-xs text-mm-muted">{msg.length}/500</span>
        <button className="mm-btn" disabled={busy || !msg.trim()} data-testid="supporter-note-send">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}Send note</button>
      </div>
    </form>
  );
};
